import LoadingButton from "@/components/LoadingButton/LoadingButton";
import DefaultTimerButton from "@/components/TimerButton/DefaultTimerButton/DefaultTimerButton";
import { VenueReservation } from "@/graphql/operations";
import useLocalization from "@/hooks/useLocalization";
import useReservation, {
  getReservationStatusLowerCase,
} from "@/hooks/useReservation";
import { parseDateString } from "@/tools/date/conversion";
import { ButtonProps, useTheme } from "@mui/material";
import { resetReservationGraceTimeMilliseconds } from "@shared/constants";
import { format } from "date-fns";
import { useMemo } from "react";

type ResetReservationButtonProps = {
  reservation: VenueReservation;
  disabled?: boolean;
  venuePortalUserIdToResetTo?: string;
  buttonSx?: ButtonProps["sx"];
};
export default function ResetReservationButton({
  reservation,
  disabled,
  venuePortalUserIdToResetTo,
  buttonSx,
}: ResetReservationButtonProps) {
  const theme = useTheme();
  const { t, adapterLocale } = useLocalization();
  const {
    resetReservation,
    editVenueReservationAnswerMutationResponse: { loading: resetting },
  } = useReservation();

  const resetReservationLabel = useMemo(
    () =>
      t("command:resetWhat", {
        interpolation: {
          what: t("nouns:reservation"),
        },
      }),
    [t],
  );

  return (
    <DefaultTimerButton
      sx={{
        fontWeight: theme.custom.reservationSummaryFontWeight,
        ...buttonSx,
      }}
      start={
        reservation.answer
          ? reservation.reservationAnswerReceived
            ? parseDateString(reservation.reservationAnswerReceived).getTime()
            : undefined
          : undefined
      }
      disabled={disabled}
      disableTimer={disabled}
      pause={!reservation.answer}
      fullWidth
      variant="contained"
      duration={resetReservationGraceTimeMilliseconds / 2}
      onClick={() =>
        resetReservation(reservation.id, venuePortalUserIdToResetTo)
      }
      CustomButton={LoadingButton}
      thickness={4}
      aria-bales={t("command:resetWhat", {
        interpolation: {
          what: t("nouns:reservation"),
        },
      })}
      timeUpComponent={
        resetting
          ? resetReservationLabel
          : reservation.answer
            ? `${t(
                `reservation:${
                  reservation.answeredBy
                    ? "answeredAtWhenByWho"
                    : "answeredAtWhen"
                }`,
                {
                  interpolation: {
                    answer: t(
                      `reservation:${getReservationStatusLowerCase(reservation)}`,
                    ),
                    when: format(
                      parseDateString(reservation.reservationAnswerReceived!),
                      "PP",
                      { locale: adapterLocale },
                    ),
                    who:
                      reservation.answeredBy === "MagicalTrip"
                        ? t("nouns:MagicalTrip")
                        : reservation.answeredBy,
                  },
                },
              )}`
            : t("reservation:unanswered")
      }
    >
      {resetReservationLabel}
    </DefaultTimerButton>
  );
}
